import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { CUSTOM_VOICE, CUSTOM_VOICE_INTRO } from '@goaltree/backend/convex/lib/coach'
import { useAction, useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import i18next from 'i18next'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useCoachIdentity } from '@/hooks/use-coach-name'
import { optimisticApproveDeadlines } from '@/lib/coach-optimistic'
import { todayKey } from '@/lib/day'
import { getUsageLimit, handleUsageLimit } from '@/lib/usage-limit'

// Module-level cache of the coach history so a fresh mount (switching routes,
// where each renders its own CoachChatSheet) renders the last known messages
// instantly instead of flashing the typing indicator while the query reloads.
let lastKnownHistory: FunctionReturnType<typeof api.coachHistory.getHistory> | undefined

// Message ids already auto-played aloud — shared across all hook instances so
// multiple mounted CoachChatSheets don't voice the same reply simultaneously.
const autoSpokenIds = new Set<string>()

export type CoachAction = {
  type: string
  description: string
  taskId?: Id<'nodes'>
  commitmentId?: Id<'coachCommitments'>
  undo?: 'uncomplete' | 'complete' | 'remove' | 'habitTime' | 'commitment'
  undone?: boolean
}

export type PendingDeadline = {
  taskId: string
  taskTitle: string
  dueDateTimestamp: number
  dueDateLabel: string
  approved?: boolean
}

type HistoryMessage = FunctionReturnType<typeof api.coachHistory.getHistory>[number]
export type CoachTaskCard = NonNullable<HistoryMessage['taskCards']>[number]

export type ChatMessage = {
  _id?: string
  role: 'user' | 'assistant'
  content: string
  actions?: CoachAction[]
  isOpener?: boolean
  style?: string
  // Who wrote it — older messages keep their coach after a switch.
  coachVoice?: string
  streaming?: boolean
  pendingApproval?: boolean
  pendingDeadlines?: PendingDeadline[]
  callout?: string
  suggestions?: string[]
  taskCards?: CoachTaskCard[]
  // Events the reply points at: read-only reference cards.
  eventCards?: NonNullable<HistoryMessage['eventCards']>
  taskGoal?: { goalId: Id<'nodes'>; openCount: number }
  imageUrl?: string | null
  mealEstimate?: HistoryMessage['mealEstimate']
}

export function localDateKey() {
  return todayKey()
}

// Leaked tool-call syntax the model sometimes emits into prose.
const LEAKED_CALL_RE =
  /<function[=\s][^>]*>[\s\S]*?<\/function>|<function_calls>[\s\S]*?<\/function_calls>|\[[a-z_]+\]\s*\{(?:[^{}]|\{[^{}]*\})*\}/g
// Internal task IDs the model sometimes echoes, e.g. "(taskId: j970…)".
const TASK_ID_RE = /\s*\(?\s*taskId:\s*[a-z0-9]{16,}\s*\)?/gi

function sanitizeCoachText(text: string): string {
  return text.replace(LEAKED_CALL_RE, '').replace(TASK_ID_RE, '').trim()
}

// WebSocket streaming TTS is OFF by default — the chunked HTTP path has tightly
// synced karaoke (driven by each clip's real duration), whereas streamed audio
// can't be word-synced reliably. Opt in with VITE_TTS_STREAMING='1'.
const TTS_STREAMING = import.meta.env.VITE_TTS_STREAMING === '1'

// Decode base64 16-bit little-endian mono PCM (xAI streaming TTS at 24 kHz).
function base64ToPcmFloat32(b64: string): Float32Array {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  const view = new DataView(bytes.buffer)
  const n = Math.floor(bytes.length / 2)
  const out = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const s = view.getInt16(i * 2, true)
    out[i] = s < 0 ? s / 0x8000 : s / 0x7fff
  }
  return out
}

// One TTS chunk per sentence — small chunks synthesize fast and in parallel, so
// the first plays quickly and later ones are ready by their turn. Tiny sentences
// are merged so we don't spawn a clip for a two-word fragment.
function chunkForTts(text: string): string[] {
  const sentences = text.match(/[^.!?]+[.!?]+\s*|[^.!?]+$/g)
  if (!sentences || sentences.length <= 1) return [text]
  const chunks: string[] = []
  let cur = ''
  for (const s of sentences) {
    cur += s
    if (cur.trim().length >= 40) {
      chunks.push(cur)
      cur = ''
    }
  }
  if (cur.trim()) chunks.push(cur)
  return chunks.length ? chunks : [text]
}

// The lock-screen / OS media controls fall back to the PWA app icon (still the old
// "G") unless we set MediaSession artwork. Render the brand bullseye to a PNG once
// and reuse it.
let cachedArtwork: string | null = null
function bullseyeArtwork(): string | null {
  if (cachedArtwork) return cachedArtwork
  if (typeof document === 'undefined') return null
  const s = 512
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const k = s / 96 // brand mark is authored on a 96×96 viewBox
  const cx = 48 * k
  // Rounded lime square
  ctx.fillStyle = '#a8ff3e'
  const r = 22 * k
  ctx.beginPath()
  ctx.roundRect(0, 0, s, s, r)
  ctx.fill()
  // Concentric rings + centre dot
  ctx.strokeStyle = '#1a2600'
  ctx.fillStyle = '#1a2600'
  ctx.beginPath()
  ctx.arc(cx, cx, 20 * k, 0, Math.PI * 2)
  ctx.lineWidth = 5 * k
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cx, 10 * k, 0, Math.PI * 2)
  ctx.lineWidth = 4 * k
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cx, 3 * k, 0, Math.PI * 2)
  ctx.fill()
  cachedArtwork = canvas.toDataURL('image/png')
  return cachedArtwork
}

function setCoachMediaSession(title: string) {
  const ms = typeof navigator !== 'undefined' ? navigator.mediaSession : undefined
  if (!ms || typeof MediaMetadata === 'undefined') return
  const art = bullseyeArtwork()
  ms.metadata = new MediaMetadata({
    title,
    artist: 'Goaltree',
    ...(art ? { artwork: [{ src: art, sizes: '512x512', type: 'image/png' }] } : {}),
  })
}

export function useCoachChat(opts?: {
  autoVoice?: boolean
  voice?: string
  style?: string
  threadId?: Id<'coachThreads'>
}) {
  const coachChatAction = useAction(api.voice.coachChat)
  const introAction = useAction(api.voice.getIntroAudio)
  const { customName } = useCoachIdentity()
  const speakAction = useAction(api.voice.speak)
  const createTtsTicket = useMutation(api.voiceStream.createTtsTicket)
  const approveDeadlinesMutation = useMutation(
    api.coachHistory.approveDeadlines,
  ).withOptimisticUpdate(optimisticApproveDeadlines)
  const dbHistoryRaw = useQuery(api.coachHistory.getHistory, {
    localDate: localDateKey(),
    ...(opts?.threadId ? { threadId: opts.threadId } : {}),
  })
  if (dbHistoryRaw !== undefined) lastKnownHistory = dbHistoryRaw
  // Fall back to the cached copy while a remount's query is still in flight.
  const dbHistory = dbHistoryRaw ?? lastKnownHistory
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [requiresUpgrade, setRequiresUpgrade] = useState(false)
  const [streamingId, setStreamingId] = useState<string | null>(null)
  const [streamedText, setStreamedText] = useState('')
  const [optimisticUserMsg, setOptimisticUserMsg] = useState<string | null>(null)
  const [speakingId, setSpeakingId] = useState<string | null>(null)
  // A message whose audio is being synthesized before its first auto-play — we
  // hold the text (typing indicator) until audio starts so the two stay in sync.
  const [voicePendingId, setVoicePendingId] = useState<string | null>(null)
  // Karaoke highlight: the message being spoken aloud + how many of its chars
  // have been voiced so far (drives per-word highlighting in the transcript).
  const [spokenId, setSpokenId] = useState<string | null>(null)
  const [spokenChars, setSpokenChars] = useState(0)
  // A persona intro preview is currently playing (drives the avatar animation).
  const [previewPlaying, setPreviewPlaying] = useState(false)

  const revealedIdsRef = useRef<Set<string>>(new Set())
  // ids whose text streamed in live (so we skip the typewriter and don't hide
  // already-shown text when voicing them).
  const streamedIdsRef = useRef<Set<string>>(new Set())
  const hasInitializedRef = useRef(false)
  // Thread the reveal state was initialized for; null while that thread is empty.
  const initThreadRef = useRef<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const rafRef = useRef<number | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Cache synthesized audio per message so replaying it doesn't re-synthesize
  // (which would cost money and consume a monthly play slot).
  const audioCacheRef = useRef<Map<string, { audio: string; contentType: string }>>(new Map())
  // In-flight intro fetches, so a prefetch and a play share one request.
  const introFetchRef = useRef<Map<string, Promise<{ audio: string; contentType: string }>>>(
    new Map(),
  )
  // Bumped on every stop/new-play so a synth request that resolves after it was
  // superseded (rapid taps) doesn't start a second overlapping audio.
  const playTokenRef = useRef(0)
  // Resolves the currently-awaited chunk playback so stopSpeaking unblocks it.
  const chunkResolveRef = useRef<(() => void) | null>(null)
  // Streaming-only (WS proxy + Web Audio PCM scheduling).
  const audioCtxRef = useRef<AudioContext | null>(null)
  const pcmCursorRef = useRef(0)
  const pcmSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())
  const wsRef = useRef<WebSocket | null>(null)
  const autoVoiceRef = useRef(false)
  const voiceRef = useRef('')
  const styleRef = useRef('')
  const threadIdRef = useRef<Id<'coachThreads'> | undefined>(undefined)
  threadIdRef.current = opts?.threadId
  autoVoiceRef.current = !!opts?.autoVoice
  voiceRef.current = opts?.voice ?? ''
  styleRef.current = opts?.style ?? ''

  function stopSpeaking() {
    playTokenRef.current += 1
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    // Streaming teardown (WS + scheduled PCM sources).
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        // already closed
      }
      wsRef.current = null
    }
    for (const src of pcmSourcesRef.current) {
      try {
        src.stop()
      } catch {
        // already stopped
      }
    }
    pcmSourcesRef.current.clear()
    if (audioCtxRef.current) pcmCursorRef.current = audioCtxRef.current.currentTime
    // Unblock any in-progress chunk sequence (it bails on the token mismatch).
    if (chunkResolveRef.current) {
      chunkResolveRef.current()
      chunkResolveRef.current = null
    }
    setVoicePendingId(null)
    setStreamingId(null)
    setSpeakingId(null)
    setSpokenId(null)
    setSpokenChars(0)
    setPreviewPlaying(false)
  }

  // Speak an assistant message aloud (chunked xAI TTS): the first sentence plays
  // quickly while later chunks synthesize in the background. Toggles off if
  // already playing. Throws on failure so the caller can surface limit/Pro.
  // Karaoke: the spoken-char count advances with playback (auto-play + replay);
  // synthesized chunks are cached so replays don't re-synthesize or re-charge.
  async function speak(id: string, text: string, reveal = false) {
    if (speakingId === id) {
      stopSpeaking()
      return
    }
    stopSpeaking()
    const token = playTokenRef.current
    setSpeakingId(id)
    if (reveal) setVoicePendingId(id)
    // Show the brand bullseye (not the old app-icon "G") on the OS media controls.
    setCoachMediaSession('Coach')

    const voice = voiceRef.current
    const style = styleRef.current
    // One chunk per sentence: the first plays almost immediately (fast first
    // audio) while the rest synthesize in parallel, so they're ready by their
    // turn. Any residual gap lands on a natural sentence boundary.
    const chunks = chunkForTts(text)
    const fullText = chunks.join('')
    const estTotalSec = Math.max(1.2, text.length / 13)

    const finalize = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (reveal) setVoicePendingId((cur) => (cur === id ? null : cur))
      setSpokenId(null)
      setSpokenChars(0)
      setSpeakingId((cur) => (cur === id ? null : cur))
    }

    // Chunked HTTP TTS (the reliable default). First sentence plays fast; ALL
    // remaining chunks are synthesized in parallel up front so they're ready
    // before the previous one finishes (no gap between sentences). Only chunk 0
    // counts against the monthly cap.
    const playChunked = async () => {
      const fetchChunk = (i: number) => {
        const key = `${id}:${i}:${voice}:${style}`
        const cached = audioCacheRef.current.get(key)
        if (cached) return Promise.resolve(cached)
        return speakAction({
          text: chunks[i],
          voice: voice || undefined,
          style: style || undefined,
          preview: i > 0,
        }).then((d) => {
          audioCacheRef.current.set(key, d)
          return d
        })
      }
      // Kick off every chunk's synthesis immediately (parallel), then play in
      // order. Chunk 0 may reject (surfaces limit/Pro); later chunks resolve to
      // null on failure so one bad chunk doesn't abort the whole reply.
      const fetches = chunks.map((_, i) =>
        i === 0 ? fetchChunk(0) : fetchChunk(i).catch(() => null),
      )
      let base = 0
      for (let i = 0; i < chunks.length; i++) {
        let data: { audio: string; contentType: string } | null
        try {
          data = await fetches[i]
        } catch (e) {
          if (i === 0) throw e
          break
        }
        if (playTokenRef.current !== token) return
        if (!data) {
          if (i === 0) throw new Error('TTS failed')
          break // a later chunk failed — stop gracefully, keep what played
        }
        const el = new Audio(`data:${data.contentType};base64,${data.audio}`)
        audioRef.current = el
        const chunkLen = chunks[i].length
        const myBase = base
        await new Promise<void>((resolve) => {
          const done = () => {
            if (rafRef.current) {
              cancelAnimationFrame(rafRef.current)
              rafRef.current = null
            }
            chunkResolveRef.current = null
            resolve()
          }
          chunkResolveRef.current = done
          el.onended = done
          el.onerror = done
          const step = () => {
            if (audioRef.current !== el) return
            const dur = el.duration
            const total = dur && Number.isFinite(dur) ? dur : Math.max(0.8, chunkLen / 13)
            if (reveal) setVoicePendingId((cur) => (cur === id ? null : cur))
            setSpokenId(id)
            const frac = Math.min(1, el.currentTime / total)
            setSpokenChars(Math.min(fullText.length, myBase + Math.ceil(frac * chunkLen)))
            if (!el.ended && !el.paused) rafRef.current = requestAnimationFrame(step)
          }
          el.onplaying = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
            rafRef.current = requestAnimationFrame(step)
          }
          el.play().catch(done)
        })
        if (playTokenRef.current !== token) return
        base += chunkLen
        setSpokenChars(base)
      }
      finalize()
    }

    // Streaming TTS via the WS proxy. Returns true if it played audio (finalized),
    // false to fall back to chunked. Gapless PCM scheduled with Web Audio.
    const playStreaming = async (ticket: string, wsUrl: string): Promise<boolean> => {
      let ctx = audioCtxRef.current
      if (!ctx || ctx.state === 'closed') {
        const AudioCtx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        ctx = new AudioCtx()
        audioCtxRef.current = ctx
      }
      await ctx.resume().catch(() => {})
      let started = false
      let playStart = 0
      let gotAudio = false
      // Set once audio.done arrives: the true total audio duration (end of the
      // last scheduled buffer). Until then, karaoke uses the char-rate estimate.
      let finalDuration = 0
      let revealed = 0 // monotonic — text must never retract
      const schedule = (f32: Float32Array) => {
        if (!ctx || f32.length === 0) return
        const buf = ctx.createBuffer(1, f32.length, 24000)
        buf.getChannelData(0).set(f32)
        const src = ctx.createBufferSource()
        src.buffer = buf
        src.connect(ctx.destination)
        const startAt = Math.max(ctx.currentTime, pcmCursorRef.current)
        if (!started) {
          started = true
          playStart = startAt
          if (reveal) setVoicePendingId((cur) => (cur === id ? null : cur))
          setSpokenId(id)
        }
        src.start(startAt)
        pcmCursorRef.current = startAt + buf.duration
        pcmSourcesRef.current.add(src)
        src.onended = () => pcmSourcesRef.current.delete(src)
      }
      const tick = () => {
        if (playTokenRef.current !== token) return
        if (started && ctx) {
          const played = Math.max(0, ctx.currentTime - playStart)
          const total = finalDuration > 0 ? finalDuration : estTotalSec
          const next = Math.min(text.length, Math.floor((played / total) * text.length))
          if (next > revealed) revealed = next
          setSpokenChars(revealed)
        }
        rafRef.current = requestAnimationFrame(tick)
      }
      const awaitPlayout = (resolve: () => void) => {
        const check = () => {
          if (playTokenRef.current !== token || !ctx || ctx.currentTime >= pcmCursorRef.current) {
            resolve()
            return
          }
          setTimeout(check, 80)
        }
        setTimeout(check, 80)
      }
      rafRef.current = requestAnimationFrame(tick)
      try {
        await new Promise<void>((resolve, reject) => {
          let done = false
          const ws = new WebSocket(
            `${wsUrl.replace(/\/$/, '')}/tts?ticket=${encodeURIComponent(ticket)}`,
          )
          wsRef.current = ws
          const timer = setTimeout(() => {
            if (!gotAudio && !done) {
              done = true
              try {
                ws.close()
              } catch {
                // already closing
              }
              reject(new Error('TTS stream timeout'))
            }
          }, 6000)
          chunkResolveRef.current = () => {
            done = true
            clearTimeout(timer)
            resolve()
          }
          ws.onmessage = (ev) => {
            if (typeof ev.data !== 'string' || playTokenRef.current !== token) return
            let m: { type?: string; delta?: string; error?: string }
            try {
              m = JSON.parse(ev.data)
            } catch {
              return
            }
            if (m.type === 'audio.delta' && typeof m.delta === 'string') {
              const f32 = base64ToPcmFloat32(m.delta)
              if (f32.length) {
                gotAudio = true
                clearTimeout(timer)
                schedule(f32)
              }
            } else if (m.type === 'audio.done') {
              done = true
              clearTimeout(timer)
              // All chunks are scheduled now → the play cursor marks the true end.
              if (ctx && playStart) finalDuration = Math.max(0, pcmCursorRef.current - playStart)
              try {
                ws.close()
              } catch {
                // already closing
              }
              awaitPlayout(resolve)
            } else if (m.type === 'error') {
              clearTimeout(timer)
              reject(new Error(m.error ?? 'TTS error'))
            }
          }
          ws.onerror = () => {
            if (!done) {
              clearTimeout(timer)
              reject(new Error('TTS connection error'))
            }
          }
          ws.onclose = () => {
            if (!done) {
              clearTimeout(timer)
              if (gotAudio) {
                if (ctx && playStart) finalDuration = Math.max(0, pcmCursorRef.current - playStart)
                awaitPlayout(resolve)
              } else reject(new Error('TTS closed'))
            }
          }
        })
        if (playTokenRef.current === token) finalize()
        return true
      } catch (err) {
        if (gotAudio) {
          if (playTokenRef.current === token) finalize()
          return true
        }
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        if (wsRef.current) {
          try {
            wsRef.current.close()
          } catch {
            // already closed
          }
          wsRef.current = null
        }
        console.warn('[coach] TTS stream failed, falling back to chunked HTTP:', err)
        return false
      }
    }

    try {
      if (TTS_STREAMING) {
        let minted: { ticket: string; wsUrl: string } | null = null
        try {
          minted = await createTtsTicket({
            text,
            voice: voice || undefined,
            style: style || undefined,
          })
        } catch (mintErr) {
          if (getUsageLimit(mintErr)) throw mintErr
          // streaming unavailable → chunked fallback below
        }
        if (playTokenRef.current !== token) return
        if (minted && (await playStreaming(minted.ticket, minted.wsUrl))) return
      }
      await playChunked()
    } catch (e) {
      finalize()
      throw e
    }
  }

  // Fetch a persona intro clip (server-cached), deduped per voice/style so a
  // prefetch already in flight is reused by a play instead of re-requested.
  function fetchIntro(voice: string | undefined, style: string | undefined) {
    const locale = i18next.language
    const key = `intro:${voice ?? ''}:${style ?? ''}:${locale}:${customName ?? ''}`
    const cached = audioCacheRef.current.get(key)
    if (cached) return Promise.resolve(cached)
    const inflight = introFetchRef.current.get(key)
    if (inflight) return inflight
    // The custom voice has no shared, pre-cached intro (its clip is per-user), so
    // synthesize a short line on the fly via the quota-exempt preview path.
    const p = (
      voice === CUSTOM_VOICE
        ? speakAction({ text: CUSTOM_VOICE_INTRO, voice: CUSTOM_VOICE, preview: true })
        : introAction({ voice: voice || undefined, style: style || undefined, locale })
    )
      .then((data) => {
        audioCacheRef.current.set(key, data)
        return data
      })
      .finally(() => introFetchRef.current.delete(key))
    introFetchRef.current.set(key, p)
    return p
  }

  // Warm the cache for a set of voices at a style (e.g. when the coach opens),
  // so opening the persona picker or switching voices plays instantly.
  function prefetchIntros(voices: string[], style: string | undefined) {
    for (const voice of voices) void fetchIntro(voice, style).catch(() => {})
  }

  // Play a short persona intro (e.g. "Hi, I'm Leo…") for a specific voice/style.
  // Quota-exempt preview; served from cache when prefetched. Best-effort.
  async function speakPreview(voice: string | undefined, style: string | undefined) {
    stopSpeaking()
    const token = playTokenRef.current
    setPreviewPlaying(true)
    try {
      const data = await fetchIntro(voice, style)
      // A newer tap (or stop) superseded this one — don't start a second audio.
      if (playTokenRef.current !== token) return
      const el = new Audio(`data:${data.contentType};base64,${data.audio}`)
      audioRef.current = el
      const clear = () => {
        if (playTokenRef.current === token) setPreviewPlaying(false)
      }
      el.onended = clear
      el.onerror = clear
      await el.play()
    } catch {
      if (playTokenRef.current === token) setPreviewPlaying(false)
    }
  }

  const hasLoaded = dbHistory !== undefined

  const dbMessages: ChatMessage[] = (dbHistory ?? [])
    .map((m) => ({
      _id: m._id as string,
      role: m.role,
      content: sanitizeCoachText(m.content),
      actions: m.actions,
      isOpener: m.isOpener,
      style: (m as { style?: string }).style,
      streaming: (m as { streaming?: boolean }).streaming,
      pendingApproval: m.pendingApproval,
      pendingDeadlines: m.pendingDeadlines,
      callout: m.callout,
      suggestions: m.suggestions,
      taskCards: m.taskCards,
      eventCards: m.eventCards,
      taskGoal: m.taskGoal,
      imageUrl: m.imageUrl,
      mealEstimate: m.mealEstimate,
    }))
    // Keep a just-created streaming placeholder even before its first token.
    .filter((m) => m.content.length > 0 || m.streaming || m.imageUrl)

  // The coach reply is currently streaming in — the growing text stands in for
  // the typing indicator.
  const isStreamingReply = dbMessages.some((m) => m.streaming)

  const lastDbUserMsg = [...(dbHistory ?? [])].reverse().find((m) => m.role === 'user')
  const showOptimistic = !!optimisticUserMsg && lastDbUserMsg?.content !== optimisticUserMsg

  const history: ChatMessage[] = showOptimistic
    ? [...dbMessages, { role: 'user', content: optimisticUserMsg! }]
    : dbMessages

  const exchangeCount = history.filter((m) => m.role === 'assistant' && !m.isOpener).length

  useEffect(() => {
    if (!optimisticUserMsg || !dbHistory) return
    const lastUserMsg = [...dbHistory].reverse().find((m) => m.role === 'user')
    if (lastUserMsg?.content === optimisticUserMsg) setOptimisticUserMsg(null)
  }, [dbHistory, optimisticUserMsg])

  // useLayoutEffect (not useEffect): set the reveal/hold state before the
  // browser paints, so a new coach message doesn't flash its full content for a
  // frame before the reveal (voice/typewriter) hides it.
  useLayoutEffect(() => {
    if (!dbHistory) return

    const lastMsg = dbHistory[dbHistory.length - 1]
    // Switching chats loads another thread's history — treat it as already seen
    // rather than voicing/typing its last reply as if it just arrived.
    const threadKey = lastMsg ? String(lastMsg.threadId ?? 'default') : null
    const switched =
      threadKey !== null &&
      (initThreadRef.current === null
        ? lastMsg.createdAt < Date.now() - 120_000
        : threadKey !== initThreadRef.current)
    initThreadRef.current = threadKey
    if (!hasInitializedRef.current || switched) {
      hasInitializedRef.current = true
      dbHistory.forEach((m) => revealedIdsRef.current.add(m._id as string))
      return
    }

    if (!lastMsg || lastMsg.role !== 'assistant') return
    const id = lastMsg._id as string
    // Text streams in live from the model — don't reveal/voice until it's done.
    // Remember it streamed so we skip the typewriter (text is already on screen).
    if (lastMsg.streaming) {
      streamedIdsRef.current.add(id)
      return
    }
    if (revealedIdsRef.current.has(id)) return
    revealedIdsRef.current.add(id)

    const fullText = sanitizeCoachText(lastMsg.content)
    const wasStreamed = streamedIdsRef.current.has(id)

    // When the coach voice auto-plays, let the audio drive the reveal. For a
    // message that streamed in live, don't hide the already-shown text (reveal
    // false) — karaoke still highlights along with the audio. Module-level guard:
    // several CoachChatSheets can be mounted+open at once and must not each
    // auto-play the same reply (two voices talking over each other).
    if (autoVoiceRef.current) {
      if (!autoSpokenIds.has(id)) {
        autoSpokenIds.add(id)
        void speak(id, fullText, !wasStreamed).catch(() => {})
      }
      return
    }

    // Text already appeared live — no typewriter needed.
    if (wasStreamed) return

    const charsPerTick = Math.max(1, Math.ceil(fullText.length / 80))
    let pos = 0
    setStreamingId(id)
    setStreamedText('')
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      pos += charsPerTick
      if (pos >= fullText.length) {
        setStreamedText(fullText)
        setStreamingId(null)
        clearInterval(intervalRef.current!)
        intervalRef.current = null
      } else {
        setStreamedText(fullText.slice(0, pos))
      }
    }, 16)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [dbHistory])

  async function send(message: string, displayMessage?: string) {
    if (!message.trim() || isLoading) return
    setIsLoading(true)
    setError(null)
    setOptimisticUserMsg(displayMessage ?? message)
    try {
      const result = await coachChatAction({
        message,
        locale: i18next.language,
        tzOffset: new Date().getTimezoneOffset(),
        ...(threadIdRef.current ? { threadId: threadIdRef.current } : {}),
      })
      setOptimisticUserMsg(null)
      if (result?.requiresUpgrade) {
        setRequiresUpgrade(true)
      }
    } catch (err) {
      setOptimisticUserMsg(null)
      if (!handleUsageLimit(err)) setError('Could not get a response. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  async function approveDeadline(messageId: string, taskId?: string) {
    await approveDeadlinesMutation({
      messageId: messageId as Id<'coachMessages'>,
      ...(taskId ? { taskIds: [taskId] } : {}),
    })
  }

  function reset() {
    setError(null)
    setRequiresUpgrade(false)
  }

  return {
    send,
    approveDeadline,
    isLoading: isLoading || !hasLoaded,
    error,
    history,
    exchangeCount,
    requiresUpgrade,
    reset,
    hasLoaded,
    isStreamingReply,
    streamingId,
    streamedText,
    speak,
    speakPreview,
    prefetchIntros,
    stopSpeaking,
    speakingId,
    voicePendingId,
    spokenId,
    spokenChars,
    previewPlaying,
  }
}
